import {
  useCreate,
  useDataProvider,
  useNavigation,
  useNotification,
  useUpdate,
} from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import type {
  NotificationRuleEditFormType,
  NotificationRuleValueType,
} from "@/pages/tina/notification-rules/schemas/notification-rule.form";
import type { NotificationRuleRead } from "@/types/tina";

const RESOURCE = "tina-notification-rules";

// The orchestrator forbids extra fields (extra="forbid"). Only the editable
// columns may be sent on POST/PATCH — never id/created_*/updated_* (id rides
// in the URL). `updated_by` is left to a follow-up (curator identity).
const toPayload = (values: NotificationRuleEditFormType) => ({
  verdict: values.verdict,
  value: values.value,
  explanation: values.explanation,
  enabled: values.enabled,
});

// Outcome of a multi-value create: which values landed and which (value, error)
// pairs failed (e.g. a duplicate (verdict, value) 409s from the orchestrator).
export interface CreateManyResult {
  created: string[];
  failed: { value: string; error: string }[];
}

export const useNotificationRulesRepository = () => {
  const { getOne } = useDataProvider()("tina");
  const { mutateAsync: createAsync } = useCreate({
    resource: RESOURCE,
    dataProviderName: "tina",
  });
  const { mutateAsync: updateAsync } = useUpdate({
    resource: RESOURCE,
    dataProviderName: "tina",
  });
  const { open } = useNotification();
  const { edit } = useNavigation();

  async function get(id: string): Promise<NotificationRuleRead> {
    const response = await getOne({ resource: RESOURCE, id });
    return response as unknown as NotificationRuleRead;
  }

  async function create(values: NotificationRuleEditFormType): Promise<string> {
    try {
      const response = await createAsync({
        resource: RESOURCE,
        dataProviderName: "tina",
        values: toPayload(values),
      });
      const id = (response.data as NotificationRuleRead).id;
      open?.({
        type: "success",
        message: "Notification rule created",
        description: `Created ${values.verdict} = ${values.value}`,
      });
      edit(RESOURCE, id, "replace");
      return id;
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to create notification rule",
        description: toHumanErrorMessage(error),
      });
      throw error;
    }
  }

  async function update(values: NotificationRuleEditFormType): Promise<string> {
    const id = values.id;
    if (!id) {
      throw new Error("Cannot update a notification rule without an id");
    }
    try {
      const response = await updateAsync({
        resource: RESOURCE,
        dataProviderName: "tina",
        id,
        values: toPayload(values),
      });
      open?.({
        type: "success",
        message: "Notification rule updated",
        description: `Updated ${values.verdict} = ${values.value}`,
      });
      return (response.data as NotificationRuleRead).id ?? id;
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to update notification rule",
        description: toHumanErrorMessage(error),
      });
      throw error;
    }
  }

  // Create one flat row per (value, explanation) pair, sharing the given verdict
  // and enabled flag. Every pair is attempted independently so one failure (e.g.
  // a duplicate 409) never blocks the rest; the aggregate outcome is returned so
  // the caller can surface a single toast. Refine's per-call notifications are
  // suppressed here (the caller owns the summary toast).
  async function createMany(
    verdict: string,
    enabled: boolean,
    pairs: NotificationRuleValueType[],
  ): Promise<CreateManyResult> {
    const created: string[] = [];
    const failed: { value: string; error: string }[] = [];
    for (const pair of pairs) {
      try {
        await createAsync({
          resource: RESOURCE,
          dataProviderName: "tina",
          values: {
            verdict,
            value: pair.value,
            explanation: pair.explanation,
            enabled,
          },
          successNotification: false,
          errorNotification: false,
        });
        created.push(pair.value);
      } catch (error) {
        failed.push({ value: pair.value, error: toHumanErrorMessage(error) });
      }
    }
    return { created, failed };
  }

  return { get, create, update, createMany };
};
